/*=========================================================================================
  File Name: recaptcha.ts
  Description: Integracao com o Google reCAPTCHA v3 (invisivel, baseado em score).

  O script do Google e carregado sob demanda — somente quando um formulario protegido
  e aberto — para nao pesar no bundle nem no carregamento das demais paginas.

  Se VUE_APP_RECAPTCHA_SITE_KEY nao estiver definida, tudo vira no-op e executeRecaptcha()
  devolve null. Assim o ambiente local continua funcionando sem chave configurada.

  O token e de uso unico e expira em 2 minutos: por isso executeRecaptcha() deve ser
  chamado no momento do submit, nunca no mounted().
==========================================================================================*/

const SITE_KEY = process.env.VUE_APP_RECAPTCHA_SITE_KEY || ''
const SCRIPT_ID = 'google-recaptcha-v3'
const SCRIPT_SRC = 'https://www.google.com/recaptcha/api.js'
const BADGE_STYLE_ID = 'google-recaptcha-v3-badge-style'
const BADGE_HIDDEN_CLASS = 'recaptcha-badge-hidden'

// grecaptcha.execute() nao rejeita quando a site key e invalida ou quando a rede ate o
// Google esta bloqueada — a promise simplesmente nunca resolve. Sem este teto o submit
// ficaria travado no spinner para sempre.
const EXECUTE_TIMEOUT_MS = 10000

let loader: Promise<any> | null = null

// Contador de paginas protegidas montadas — nao um booleano. Na troca de rota o Vue
// monta o componente novo ANTES de destruir o antigo, entao um simples show/hide
// deixaria o hide da pagina que sai sobrescrever o show da que entra, escondendo o
// selo justamente em uma pagina protegida.
let activeForms = 0

/**
 * O reCAPTCHA so entra em acao quando a site key esta configurada no .env.
 */
export function isRecaptchaEnabled(): boolean {
  return SITE_KEY !== ''
}

/**
 * O selo do reCAPTCHA e injetado pelo Google em position:fixed e fica visivel em toda a
 * SPA depois de carregado. Escondemos fora das paginas protegidas.
 *
 * Via classe no <body> e nao por estilo inline no elemento: o Google cria o selo de
 * forma assincrona, entao mexer no elemento so funciona se ele ja existir no momento
 * exato do mount. A regra CSS passa a valer sozinha quando o selo aparecer.
 */
function ensureBadgeStyle(): void {
  if (document.getElementById(BADGE_STYLE_ID)) return

  const style = document.createElement('style')
  style.id = BADGE_STYLE_ID
  style.textContent =
    `body.${BADGE_HIDDEN_CLASS} .grecaptcha-badge { visibility: hidden !important; opacity: 0 !important; }`

  document.head.appendChild(style)
}

function applyBadgeVisibility(): void {
  ensureBadgeStyle()
  document.body.classList.toggle(BADGE_HIDDEN_CLASS, activeForms <= 0)
}

export function retainBadge(): void {
  activeForms += 1
  applyBadgeVisibility()
}

export function releaseBadge(): void {
  activeForms = Math.max(0, activeForms - 1)
  applyBadgeVisibility()
}

function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(`${label} excedeu ${ms}ms`)), ms)

    promise.then(
      (value: T) => {
        clearTimeout(timer)
        resolve(value)
      },
      (error: Error) => {
        clearTimeout(timer)
        reject(error)
      }
    )
  })
}

function loadScript(): Promise<any> {
  if (loader) return loader

  loader = new Promise((resolve, reject) => {
    const win = window as any

    // Script ja presente (ex.: hot reload) — reaproveita.
    if (win.grecaptcha && win.grecaptcha.execute) {
      win.grecaptcha.ready(() => resolve(win.grecaptcha))
      return
    }

    const script = document.createElement('script')
    script.id = SCRIPT_ID
    script.src = `${SCRIPT_SRC}?render=${encodeURIComponent(SITE_KEY)}`
    script.async = true
    script.defer = true

    script.onload = () => {
      if (!win.grecaptcha) {
        loader = null
        reject(new Error('grecaptcha indisponivel apos o carregamento do script'))
        return
      }

      win.grecaptcha.ready(() => {
        applyBadgeVisibility()
        resolve(win.grecaptcha)
      })
    }

    script.onerror = () => {
      // Zera o cache para permitir nova tentativa no proximo submit.
      loader = null
      script.remove()
      reject(new Error('falha ao carregar o script do reCAPTCHA'))
    }

    document.head.appendChild(script)
  })

  return loader
}

/**
 * Carrega o script antecipadamente para que o selo ja apareca junto com o formulario.
 * Nunca rejeita: uma falha aqui nao pode quebrar a renderizacao da pagina.
 */
export function preloadRecaptcha(): Promise<void> {
  if (!isRecaptchaEnabled()) return Promise.resolve()

  return loadScript()
    .then(() => undefined)
    .catch((error: Error) => {
      console.warn('[reCAPTCHA] preload:', error.message)
    })
}

/**
 * Gera um token para a acao informada (apenas letras, numeros, "/" e "_").
 *
 * Devolve null quando o reCAPTCHA esta desabilitado ou quando o script nao pode ser
 * carregado/executado (adblock, rede). A decisao de aceitar ou nao a requisicao sem
 * token e do backend — falhar aqui bloquearia usuarios legitimos.
 */
export function executeRecaptcha(action: string): Promise<string | null> {
  if (!isRecaptchaEnabled()) return Promise.resolve(null)

  const attempt = loadScript().then((grecaptcha: any) =>
    grecaptcha.execute(SITE_KEY, { action })
  )

  return withTimeout(attempt, EXECUTE_TIMEOUT_MS, 'geracao do token')
    .then((token: string) => {
      applyBadgeVisibility()
      return token || null
    })
    .catch((error: Error) => {
      console.warn('[reCAPTCHA] nao foi possivel gerar o token:', error.message)
      return null
    })
}

export default {
  isRecaptchaEnabled,
  preloadRecaptcha,
  executeRecaptcha,
  retainBadge,
  releaseBadge
}
